import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Icon } from '@panaya/ui/p-icon';
import * as _ from 'lodash';
import { PFieldAbstractComponent } from '../p-field-abstract';

export const _defaultYesNo: PFieldToggleOptionConfig[] = [
    { id: false, name: 'No' },
    { id: true, name: 'Yes' }
];

@Component({
    selector: 'p-field-toggle',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [customClasses]="customClasses"
            [shouldShowLabel]="shouldShowLabel"
        >
            <div class="p-toggle p-paragraph-1" slot="readMode">
                <div
                    *ngFor="let option of list; let first = first; let last = last"
                    class="p-toggle-item"
                    [ngClass]="first ? 'first' : last ? 'last' : 'middle'"
                    [matTooltip]="option.getTooltip ? option.getTooltip(option[idField]) : ''"
                >
                    <input
                        [attr.automation-id]="'p-field-text_' + option[idField]"
                        [id]="'toggle-' + option[idField]"
                        [value]="option[idField]"
                        [checked]="compareValue(option[idField], value)"
                        [disabled]="option.disabled ? option.disabled(option[idField]) : true"
                        type="radio"
                        class="p-toggle-button"
                    />
                    <label [attr.for]="'toggle-' + option[idField]" class="p-toggle-label" [class.p-toggle-label-icon]="option.icon">
                        <span class="p-toggle-icon" *ngIf="option.icon; else toggleText">
                            <p-icon class="p-toggle-icon-comp" [name]="option.icon.name" [size]="option.icon.size ?? 'md'"></p-icon>
                        </span>

                        <ng-template #toggleText>
                            <span class="p-toggle-text">{{ option[nameField] }}</span>
                        </ng-template>
                    </label>
                </div>
            </div>

            <div class="p-toggle p-paragraph-1" slot="editMode">
                <div
                    *ngFor="let option of list; let first = first; let last = last"
                    class="p-toggle-item"
                    [ngClass]="first ? 'first' : last ? 'last' : 'middle'"
                    [matTooltip]="option.getTooltip ? option.getTooltip(option[idField]) : ''"
                >
                    <input
                        [attr.automation-id]="'p-field-text_' + option[idField]"
                        [id]="'toggle-' + option[idField]"
                        [value]="option[idField]"
                        (change)="changeValue(option[idField])"
                        [checked]="compareValue(option[idField], value)"
                        [disabled]="option.disabled ? option.disabled(option[idField]) : false"
                        type="radio"
                        class="p-toggle-button"
                    />
                    <label
                        [attr.for]="'toggle-' + option[idField]"
                        class="p-toggle-label"
                        [class.p-toggle-label-icon]="option.icon"
                        [class.checkedbtn]="compareValue(option[idField], value)"
                    >
                        <span class="p-toggle-icon" *ngIf="option.icon; else toggleText">
                            <p-icon class="p-toggle-icon-comp" [name]="option.icon.name" [size]="option.icon.size ?? 'md'"></p-icon>
                        </span>

                        <ng-template #toggleText>
                            <span class="p-toggle-text">{{ option[nameField] }}</span>
                        </ng-template>
                    </label>
                </div>
            </div>
        </p-field>
    `,
    styleUrls: ['./p-field-toggle.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldToggle',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldToggleComponent
        }
    ]
})
export class PFieldToggleComponent<T> extends PFieldAbstractComponent<T> {
    @Input() label?: string;
    @Input() list: PFieldToggleOptionConfig[] = _defaultYesNo;

    @Input() isMandatory = false;
    @Input() textCustomClass?: string;
    @Input() isInEditMode = true;
    @Input() customClasses?: string;
    @Input() shouldShowLabel = true;
    @Input() textInputSize?: string; // TODO: in the legacy site this can get a "L" value to determine using long input width.
    // May implement this or not according to need;
    @Input() placeholder = '';
    @Input() idField: string = 'id';
    @Input() nameField: string = 'name';

    get classes(): object {
        return {
            /*'p-field-label--toggle': true,*/
            textCustomClass: true
        };
    }

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    compareValue(value: any, v: any) {
        if (value === v) {
            return true;
        }

        return (v === '' || v === null || v === undefined) && value === false;
    }

    changeValue(value: T) {
        if (!this.isReadOnly) {
            this.value = value;
            this._onChange(value);
        }

        this.markAsTouched();
    }

    getNameById(id: number) {
        return _.find(this.list, { [this.idField]: id })?.[this.nameField] || id;
    }
}

export interface PFieldToggleOptionConfig {
    // Support for legacy weakly typed properties
    [key: string]: any;
    id: string | number | boolean;
    name: string;
    disabled?: (id: string) => boolean;
    icon?: Icon;
    getTooltip?: (id: string) => string;
}
